import React, { useState, useEffect, useRef } from "react";
import ReactSVG from "react-svg";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import moment from "moment";
import get from "lodash/get";
import baseService from "services/BaseService";
import { Link, withRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { showAudioPlayer, setAudioTrack } from "actions/audioplayer";
import permissions, { checkPermissions } from "config/permissions";
import SmartPortal from "../../SmartPortal";

const CallItem = ({ data }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const audioFileRef = useRef(null);

    const [audioFile, setAudioFile] = useState(null);
    const [createdAppeals, setCreatedAppeals] = useState([]);
    const [isAudioDropdownOpen, setIsAudioDropdownOpen] = useState(false);

    const { recordListening } = useSelector(state => ({
        audioSource: state.audioplayer.source,
        audioTitle: state.audioplayer.title,
        operations: state.user.operations,
        recordListening: checkPermissions(permissions.CallOperations.record_listening),
    }));

    useEffect(() => {
        getCreatedAppeals();
        getCallRecord();
    }, []);

    const getCreatedAppeals = () => {
        const params = {
            data: {
                interactionId: data.interactionid,
            },
        };

        baseService
            .post("interaction_appeal", params)
            .then(response => {
                if (response.success) {
                    setCreatedAppeals(response.result);
                }
            })
            .catch(console.error);
    };

    const getCallRecord = () => {
        const params = {
            data: {
                interaction_id: data.interactionid,
            },
        };

        if (recordListening) {
            baseService
                .get("appeal_interaction_proof", params)
                .then(response => {
                    if (response.success && response.result && response.result.length) {
                        setAudioFile(response.result[0]);
                    }
                })
                .catch(console.error);
        }
    };

    const onPlay = () => {
        const { rootURL, title } = audioFile;
        dispatch(setAudioTrack({ source: rootURL, title }));
        dispatch(showAudioPlayer());

        setIsAudioDropdownOpen(false);
    };

    const dateParser = value => moment(value).format("DD.MM.YYYY HH:mm:ss");

    const getConfig = () => [
        { title: t("appealHistory.beginning"), path: "startDate", parser: dateParser },
        { title: t("appealHistory.numberFrom"), path: "originator.originator" },
        { title: t("appealHistory.numberTo"), path: "operatorPhone" },
    ];

    const renderAppeal = ({ id, num, theme }) => (
        <div className="created-appeal" key={id}>
            <Link to={`/appeals/${id}/`}>
                <ReactSVG className="icon-appeal-link" src={`/data/svg/history/link.svg`} />
                {num}
            </Link>
            <div className="appealContent">{theme}</div>
        </div>
    );

    const renderRow = ({ title, path, parser }) => {
        const value = get(data, path);
        return (
            <div className="notification-row" key={path}>
                <span className="row-title">{title}:</span>
                <div className="row-content">{parser ? parser(value) : value}</div>
            </div>
        );
    };

    const renderAudio = () => {
        if (audioFile?.type === "link") {
            return (
                <a className="linkToFile" href={audioFile.rootURL} target="_blank">
                    <span className="fileName">{audioFile.title}</span>
                </a>
            );
        } else {
            return (
                <div className="audio-file" ref={audioFileRef}>
                    <ReactSVG className="icon-audio-file" src={`/data/svg/history/file.svg`} />
                    <span className="fileName">{audioFile.title}</span>
                    <div
                        className={cx("button-hover-half-circle", { active: isAudioDropdownOpen })}
                        onClick={() => setIsAudioDropdownOpen(true)}
                    >
                        <ReactSVG
                            className="button-icon button-icon-toggle"
                            src={`/data/svg/history/chevron-down.svg`}
                        />
                    </div>
                    {audioFileRef && audioFileRef.current && isAudioDropdownOpen && (
                        <SmartPortal
                            portalClassName="portal-audio-item__file"
                            targetElement={audioFileRef.current}
                            portalPlacement="bottom inner-right"
                            portalKey={audioFile.title}
                            onClickOutside={() => setIsAudioDropdownOpen(false)}
                            outerOffset={4}
                        >
                            <div className="audio-item__file-dropdown" role="menu">
                                <a
                                    className="audio-item__file-dropdown__option"
                                    target="_blank"
                                    download={audioFile.title}
                                    href={audioFile.rootURL}
                                    onClick={() => setIsAudioDropdownOpen(false)}
                                >
                                    {t("commentSection.download")}
                                </a>
                                <div className="audio-item__file-dropdown__option" onClick={onPlay}>
                                    {t("commentSection.open")}
                                </div>
                            </div>
                        </SmartPortal>
                    )}
                </div>
            );
        }
    };

    return (
        <div className="history-item">
            <div className="history-item-content">
                <div className="history-title-text">{t("history.call")}</div>
                <div className="call-content">
                    <div className="callInfo">{getConfig().map(renderRow)}</div>
                    {createdAppeals.length > 0 && (
                        <div className="call-appeals-section">
                            <span className="section-title">{t("appealHistory.sessionAppeals")}:</span>
                            <div className="created-appeals">{createdAppeals.map(renderAppeal)}</div>
                        </div>
                    )}
                    {audioFile && recordListening && (
                        <div className="notification-row">
                            <span className="row-title">{t("history.callRecord")}:</span>
                            <div className="row-content">{renderAudio()}</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

CallItem.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.number,
        startDate: PropTypes.number,
        endDate: PropTypes.number,
        operatorName: PropTypes.string,
        originator: PropTypes.shape({
            channel: PropTypes.string,
            originator: PropTypes.string,
            channelName: PropTypes.string,
        }),
        employee: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
        }),
        duration: PropTypes.number,
        interactionid: PropTypes.number,

        callRecord: PropTypes.shape({
            rootURL: PropTypes.string,
            title: PropTypes.string,
            type: PropTypes.string,
            uniqueid: PropTypes.string,
        }),
    }),
    highlight: PropTypes.func,
};

export default withRouter(CallItem);
